/**
 * Role-based reporting layers (not designations).
 * Super Admin is always the top locked layer.
 */

import {
  formatRoleLabel,
  normalizeEmployeeRole,
} from "./employeeRoleConstants";

export const ROLE_THEME = {
  admin: {
    key: "admin",
    label: "Super Admin",
    short: "Top",
    bg: "#f5f3ff",
    border: "#c4b5fd",
    text: "#5b21b6",
    accent: "#7c3aed",
    chip: "rh-role--admin",
  },
  hr: {
    key: "hr",
    label: "HR",
    short: "HR",
    bg: "#ecfeff",
    border: "#67e8f9",
    text: "#0e7490",
    accent: "#0891b2",
    chip: "rh-role--hr",
  },
  manager: {
    key: "manager",
    label: "Manager",
    short: "Mgr",
    bg: "#eff6ff",
    border: "#93c5fd",
    text: "#1d4ed8",
    accent: "#2563eb",
    chip: "rh-role--manager",
  },
  employee: {
    key: "employee",
    label: "Employee",
    short: "Team",
    bg: "#f8fafc",
    border: "#cbd5e1",
    text: "#475569",
    accent: "#64748b",
    chip: "rh-role--employee",
  },
};

/** Preset org reporting shapes — Super Admin always first. */
export const LAYER_PRESETS = {
  layers3: {
    id: "layers3",
    name: "3 layers",
    hint: "Super Admin → HR / Manager → Employee",
    layers: [
      { id: "L0", label: "Super Admin", roles: ["admin"], locked: true },
      { id: "L1", label: "HR / Manager", roles: ["hr", "manager"] },
      { id: "L2", label: "Employee", roles: ["employee"], locked: true },
    ],
  },
  layers4: {
    id: "layers4",
    name: "4 layers",
    hint: "Super Admin → HR → Manager → Employee",
    layers: [
      { id: "L0", label: "Super Admin", roles: ["admin"], locked: true },
      { id: "L1", label: "HR", roles: ["hr"] },
      { id: "L2", label: "Manager", roles: ["manager"] },
      { id: "L3", label: "Employee", roles: ["employee"], locked: true },
    ],
  },
  layersAdmin: {
    id: "layersAdmin",
    name: "With Admin mid-layer",
    hint: "Super Admin → Admin / HR → Manager → Employee",
    layers: [
      { id: "L0", label: "Super Admin", roles: ["admin"], locked: true },
      { id: "L1", label: "Admin / HR", roles: ["hr"] },
      { id: "L2", label: "Manager", roles: ["manager"] },
      { id: "L3", label: "Employee", roles: ["employee"], locked: true },
    ],
  },
};

export const DEFAULT_LAYER_PRESET_ID = "layers3";

const STORAGE_PREFIX = "brixigo.reportingLayers.v1";

function storageKey(orgKey) {
  return `${STORAGE_PREFIX}:${orgKey || "default"}`;
}

export function getRoleTheme(role) {
  const key = normalizeEmployeeRole(role);
  return ROLE_THEME[key] || ROLE_THEME.employee;
}

/** Lower number = higher in the org (Super Admin = 0). */
export function roleAuthorityRank(role) {
  const key = normalizeEmployeeRole(role);
  if (key === "admin") return 0;
  if (key === "hr") return 1;
  if (key === "manager") return 2;
  return 3;
}

/**
 * Roles that must never appear as reports under a given boss role.
 * Manager reports: hide Super Admin, HR, and Manager (already above / peer leadership).
 * HR reports: hide Super Admin (and the selected HR is excluded by id).
 */
export function excludedReportRolesForBoss(bossRole) {
  const rank = roleAuthorityRank(bossRole);
  if (rank >= 2) return ["admin", "hr", "manager"]; // manager (or below): only employees
  if (rank === 1) return ["admin"]; // HR: employees + managers (+ peer HRs filtered by picker rules)
  return ["admin"];
}

export function loadLayerConfig(orgKey) {
  try {
    const raw = localStorage.getItem(storageKey(orgKey));
    if (!raw) return clonePreset(DEFAULT_LAYER_PRESET_ID);
    const parsed = JSON.parse(raw);
    if (!parsed?.layers?.length) return clonePreset(DEFAULT_LAYER_PRESET_ID);
    return normalizeConfig(parsed);
  } catch {
    return clonePreset(DEFAULT_LAYER_PRESET_ID);
  }
}

export function saveLayerConfig(orgKey, config) {
  const next = normalizeConfig(config);
  localStorage.setItem(storageKey(orgKey), JSON.stringify(next));
  return next;
}

export function clonePreset(presetId) {
  const preset = LAYER_PRESETS[presetId] || LAYER_PRESETS[DEFAULT_LAYER_PRESET_ID];
  return normalizeConfig({
    presetId: preset.id,
    layers: preset.layers.map((l) => ({ ...l, roles: [...l.roles] })),
  });
}

function normalizeConfig(config) {
  let layers = Array.isArray(config.layers) ? config.layers.map((l, i) => ({
    id: l.id || `L${i}`,
    label: String(l.label || `Layer ${i + 1}`),
    roles: Array.isArray(l.roles) && l.roles.length
      ? l.roles.map(normalizeEmployeeRole)
      : ["employee"],
    locked: Boolean(l.locked),
  })) : [];

  if (!layers.length) return clonePreset(DEFAULT_LAYER_PRESET_ID);

  // Always keep Super Admin on top
  layers[0] = {
    ...layers[0],
    id: layers[0].id || "L0",
    label: "Super Admin",
    roles: ["admin"],
    locked: true,
  };

  // Always keep Employee as last layer
  const last = layers[layers.length - 1];
  if (!last.roles.includes("employee") || layers.length === 1) {
    layers = [
      ...layers,
      { id: `L${layers.length}`, label: "Employee", roles: ["employee"], locked: true },
    ];
  } else {
    layers[layers.length - 1] = {
      ...last,
      label: last.label || "Employee",
      roles: ["employee"],
      locked: true,
    };
  }

  return {
    presetId: config.presetId || "custom",
    layers,
  };
}

/** Expected layer index for a person's system role (0 = Super Admin). */
export function expectedLayerIndex(config, role) {
  const key = normalizeEmployeeRole(role);
  const layers = config?.layers || [];
  if (key === "admin") return 0;
  for (let i = 0; i < layers.length; i += 1) {
    if (layers[i].roles.includes(key)) return i;
  }
  // Unknown → employee layer
  return Math.max(layers.length - 1, 0);
}

/**
 * True when placing an Employee-role person directly under Super Admin
 * (skipping the usual mid layers).
 */
export function isSkippingLayers({ employeeRole, managerRole, config }) {
  const emp = normalizeEmployeeRole(employeeRole);
  const mgr = normalizeEmployeeRole(managerRole);
  if (emp !== "employee") return false;
  if (mgr !== "admin") return false;
  const layers = config?.layers || [];
  return layers.length >= 3;
}

export function midLayerLabel(config) {
  const layers = config?.layers || [];
  if (layers.length < 3) return "HR / Manager";
  return layers
    .slice(1, -1)
    .map((l) => l.label)
    .join(" / ");
}

export function isLeadershipRole(role) {
  return normalizeEmployeeRole(role) === "admin";
}

export function canEditReportingStructure(viewerRole) {
  return normalizeEmployeeRole(viewerRole) === "admin";
}

/**
 * Truncate upward chain for non–Super Admin viewers.
 * Manager/HR see up through manager-eligible people, not Super Admin above.
 */
export function filterChainForViewer(chain, viewerRole, selectedPerson) {
  const items = Array.isArray(chain) ? [...chain] : [];
  const flow = [];

  if (selectedPerson) {
    flow.push({
      employee_id: selectedPerson.employee_id,
      employee_name: selectedPerson.employee_name,
      employee_code: selectedPerson.employee_code,
      designation: selectedPerson.designation,
      department: selectedPerson.department,
      role: selectedPerson.role,
      isSelf: true,
    });
  }

  const upward = items.map((item) => ({ ...item, isSelf: false }));
  const viewer = normalizeEmployeeRole(viewerRole);

  // Super Admin & HR: full path (HR is view-only but needs the whole picture).
  if (canEditReportingStructure(viewerRole) || viewer === "hr") {
    return [...flow, ...upward];
  }

  // Manager: show up through Manager/HR — never Super Admin above.
  const capped = [];
  for (const item of upward) {
    const role = normalizeEmployeeRole(item.role);
    if (role === "admin") break;
    capped.push(item);
    if (role === "manager" || role === "hr") break;
  }
  return [...flow, ...capped];
}

export function roleBadgeLabel(role) {
  return formatRoleLabel(role);
}

export { formatRoleLabel, normalizeEmployeeRole };
